// Client-side audio gain for the tagging player.
//
// Drone recordings are often very quiet, so faint drones are inaudible at the
// raw amplitude. We boost playback in the browser (non-destructive — the S3
// audio is never modified) using a Web Audio GainNode. Two stages combine:
//   1. auto-gain  — derived from the decoded buffer's loudness (RMS), so every
//                   file lands near a consistent target level.
//   2. manual boost — a user-controlled multiplier on top of auto-gain.
// The effective gain applied to the GainNode is `autoGain * manualBoost`, with a
// DynamicsCompressor limiter downstream catching any peaks so over-boosting
// doesn't clip harshly.

// Target playback RMS (~ -20 dBFS). Quiet files are boosted toward this level.
export const TARGET_RMS = 0.1;
// Ceiling for auto-gain so a near-silent chunk's noise floor isn't slammed to
// full volume. Manual boost (and the limiter) can still go further.
export const MAX_AUTO_GAIN = 8;
// Keep auto-gain from pushing the file's true peak past this, to avoid clipping
// before the limiter. (The limiter is the final safeguard, mainly for manual boost.)
export const PEAK_CEILING = 0.97;

// Manual boost slider bounds (multipliers applied on top of auto-gain).
export const MANUAL_MIN = 0.5;
export const MANUAL_MAX = 4;
export const MANUAL_DEFAULT = 1;

const GAIN_SETTINGS_KEY = "dronetag-audio-gain";

export interface ChannelStats {
  rms: number;
  peak: number;
}

/**
 * Compute combined peak (max abs sample) and RMS across all channels of a
 * decoded buffer. Pure over Float32Array[] so it's testable without an
 * AudioContext / DOM.
 */
export function analyzeChannels(channels: Float32Array[]): ChannelStats {
  let sumSquares = 0;
  let count = 0;
  let peak = 0;
  for (const data of channels) {
    for (let i = 0; i < data.length; i++) {
      const s = data[i];
      const abs = s < 0 ? -s : s;
      if (abs > peak) peak = abs;
      sumSquares += s * s;
    }
    count += data.length;
  }
  const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
  return { rms, peak };
}

/**
 * Auto-gain multiplier from a file's RMS + peak.
 * - Boosts quiet files toward TARGET_RMS.
 * - Never attenuates (returns >= 1); manual boost handles turning things down.
 * - Clamped by MAX_AUTO_GAIN and by the peak ceiling so it won't pre-clip.
 * - Silent / invalid input → 1 (no boost), so noise floors aren't amplified.
 */
export function computeAutoGain(rms: number, peak: number): number {
  if (!Number.isFinite(rms) || rms <= 1e-6) return 1;
  const byRms = TARGET_RMS / rms;
  const byPeak = peak > 1e-6 ? PEAK_CEILING / peak : MAX_AUTO_GAIN;
  const g = Math.min(byRms, byPeak, MAX_AUTO_GAIN);
  return g > 1 ? g : 1;
}

/** Clamp a manual-boost value into the allowed slider range. */
export function clampManualBoost(value: number): number {
  if (!Number.isFinite(value)) return MANUAL_DEFAULT;
  return Math.min(MANUAL_MAX, Math.max(MANUAL_MIN, value));
}

/** Load the user's persisted manual boost (per-browser), defaulting safely. */
export function loadManualBoost(): number {
  try {
    const raw = localStorage.getItem(GAIN_SETTINGS_KEY);
    if (!raw) return MANUAL_DEFAULT;
    const parsed = JSON.parse(raw) as { manualBoost?: unknown };
    return typeof parsed.manualBoost === "number"
      ? clampManualBoost(parsed.manualBoost)
      : MANUAL_DEFAULT;
  } catch {
    return MANUAL_DEFAULT;
  }
}

/** Persist the user's manual boost. Ignores quota / privacy-mode errors. */
export function saveManualBoost(manualBoost: number): void {
  try {
    localStorage.setItem(GAIN_SETTINGS_KEY, JSON.stringify({ manualBoost }));
  } catch {
    // ignore
  }
}
